import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import localWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Button } from './button';
import { Input } from './input';

const PDFJS_VERSION = (pdfjs as any).version || '5.4.296';
const CDN_WORKER_SRC = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;
const WORKER_SOURCES = [localWorkerSrc, CDN_WORKER_SRC, `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`];
import {
  Bug,
  ChevronLeft,
  ChevronRight,
  Download,
  Expand,
  Maximize2,
  Minimize,
  PanelLeft,
  RotateCcw,
  RotateCw,
  Search,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = WORKER_SOURCES[0];

// Worker fallback: if loading the worker fails (e.g. network/CSP/MIME), swap to CDN sources.
let __workerFallbackIndex = 0;
const advanceWorkerFallback = () => {
  if (__workerFallbackIndex >= WORKER_SOURCES.length - 1) return false;
  __workerFallbackIndex += 1;
  pdfjs.GlobalWorkerOptions.workerSrc = WORKER_SOURCES[__workerFallbackIndex];
  // eslint-disable-next-line no-console
  console.warn('[pdf-viewer] Switching PDF.js worker to fallback:', pdfjs.GlobalWorkerOptions.workerSrc);
  return true;
};
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const src = (event.target as any)?.src as string | undefined;
    if (src && WORKER_SOURCES.some((s) => src.includes(s) || s.includes(src))) {
      advanceWorkerFallback();
    }
  }, true);
}

interface PDFViewerProps {
  url: string;
  fileName?: string;
  dialogOpen?: boolean;
  debugLabel?: string;
}

interface SearchHit {
  page: number;
  index: number;
}

const STORAGE_PREFIX = 'pdfViewer:';
const DEBUG_STORAGE_KEY = 'pdfViewer:debugOverlay';

const storageKey = (url: string, fileName: string) =>
  `${STORAGE_PREFIX}${fileName || ''}:${url.startsWith('blob:') ? 'blob' : url}`;

const DOCUMENT_OPTIONS = {
  cMapUrl: 'https://unpkg.com/pdfjs-dist@4.4.168/cmaps/',
  cMapPacked: true,
};

const clampScale = (value: number) => Math.min(3, Math.max(0.5, value));

const formatError = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  return 'Unknown PDF error';
};

export const PDFViewer: React.FC<PDFViewerProps> = ({
  url,
  fileName = 'document.pdf',
  dialogOpen,
  debugLabel = 'PDF Viewer',
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1);
  const [fitWidth, setFitWidth] = useState<boolean>(true);
  const [rotation, setRotation] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showThumbs, setShowThumbs] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showDebug, setShowDebug] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DEBUG_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [query, setQuery] = useState<string>('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [hitIndex, setHitIndex] = useState<number>(0);
  const [searching, setSearching] = useState<boolean>(false);
  const [containerWidth, setContainerWidth] = useState<number>(640);
  const [lastMeasuredWidth, setLastMeasuredWidth] = useState<number>(0);
  const [measurementStatus, setMeasurementStatus] = useState<'waiting' | 'ready'>('waiting');
  const [measurementSource, setMeasurementSource] = useState<'measure-shell' | 'viewport-shell' | 'scroll-container' | 'root-shell' | 'root-clamped' | 'none'>('none');
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [documentLoaded, setDocumentLoaded] = useState<boolean>(false);
  const [renderedPage, setRenderedPage] = useState<number | null>(null);
  const [mounted, setMounted] = useState<boolean>(false);
  const [renderAttempt, setRenderAttempt] = useState<number>(0);
  const [docAttempt, setDocAttempt] = useState<number>(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageShellRef = useRef<HTMLDivElement>(null);
  const widthRafRef = useRef<number | null>(null);
  const pdfDocRef = useRef<any>(null);
  const restoredRef = useRef(false);
  const pageRetryAttemptsRef = useRef<Record<string, number>>({});

  const docOptions = useMemo(() => DOCUMENT_OPTIONS, []);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(DEBUG_STORAGE_KEY, showDebug ? '1' : '0');
    } catch {
      // ignore
    }
  }, [showDebug]);

  useEffect(() => {
    pdfDocRef.current = null;
    setNumPages(0);
    setRenderedPage(null);
    setDocumentLoaded(false);
    setDocumentError(null);
    setPageError(null);
    setHits([]);
    setHitIndex(0);
    setMeasurementStatus('waiting');
    setMeasurementSource('none');
  }, [url]);

  useEffect(() => {
    restoredRef.current = false;
    try {
      const raw = localStorage.getItem(storageKey(url, fileName));
      if (raw) {
        const saved = JSON.parse(raw);
        if (typeof saved.page === 'number' && Number.isFinite(saved.page)) setPageNumber(Math.max(1, Math.floor(saved.page)));
        if (typeof saved.scale === 'number' && Number.isFinite(saved.scale)) setScale(clampScale(saved.scale));
        if (typeof saved.fitWidth === 'boolean') setFitWidth(saved.fitWidth);
        if (typeof saved.rotation === 'number' && Number.isFinite(saved.rotation)) {
          setRotation((((saved.rotation % 360) + 360) % 360));
        }
      }
    } catch {
      // ignore
    }

    const raf = requestAnimationFrame(() => {
      restoredRef.current = true;
    });

    return () => cancelAnimationFrame(raf);
  }, [url, fileName]);

  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      localStorage.setItem(
        storageKey(url, fileName),
        JSON.stringify({ page: pageNumber, scale: clampScale(scale), fitWidth, rotation }),
      );
    } catch {
      // ignore
    }
  }, [url, fileName, pageNumber, scale, fitWidth, rotation]);

  const updateContainerWidth = useCallback(() => {
    const scrollerEl = containerRef.current;
    if (!scrollerEl) return;
    const rootEl = rootRef.current;
    const asideEl = asideRef.current;
    const measureEl = measureRef.current;
    const viewportEl = viewportRef.current;

    const styles = window.getComputedStyle(scrollerEl);
    const paddingX = (parseFloat(styles.paddingLeft || '0') || 0) + (parseFloat(styles.paddingRight || '0') || 0);
    const measurementEl = measureEl ?? viewportEl ?? scrollerEl;
    const measuredWidth = measurementEl.clientWidth || measurementEl.getBoundingClientRect().width || 0;
    const viewportWidth = viewportEl?.clientWidth || viewportEl?.getBoundingClientRect().width || 0;
    const scrollerWidth = scrollerEl.clientWidth || scrollerEl.getBoundingClientRect().width || 0;
    const rootWidth = rootEl?.clientWidth || rootEl?.getBoundingClientRect().width || 0;
    const asideWidth = asideEl?.getBoundingClientRect().width || 0;
    const rootAvailableWidth = Math.max(0, rootWidth - asideWidth);
    const candidateWidths = [measuredWidth, viewportWidth, scrollerWidth].filter((value) => value > 0);
    const preferredWidth = candidateWidths.length > 0 ? Math.min(...candidateWidths) : 0;
    const effectiveWidth = rootAvailableWidth > 0
      ? (preferredWidth > 0 ? Math.min(preferredWidth, rootAvailableWidth) : rootAvailableWidth)
      : preferredWidth;
    const nextWidth = Math.max(280, Math.floor(effectiveWidth - paddingX));
    const nextSource = rootAvailableWidth > 0 && effectiveWidth === rootAvailableWidth
      ? 'root-shell'
      : rootAvailableWidth > 0 && preferredWidth > rootAvailableWidth
        ? 'root-clamped'
        : measureEl
          ? 'measure-shell'
          : viewportEl
            ? 'viewport-shell'
            : 'scroll-container';

    if (widthRafRef.current !== null) cancelAnimationFrame(widthRafRef.current);
    widthRafRef.current = requestAnimationFrame(() => {
      widthRafRef.current = null;
      setLastMeasuredWidth(nextWidth);
      setMeasurementStatus(nextWidth > 0 ? 'ready' : 'waiting');
      setMeasurementSource(nextSource);
      setContainerWidth((prev) => (Math.abs(prev - nextWidth) > 1 ? nextWidth : prev));
      console.info('[pdf-viewer] width-measure', {
        label: debugLabel,
        source: nextSource,
        measuredWidth,
        viewportWidth,
        rootWidth,
        asideWidth,
        rootAvailableWidth,
        effectiveWidth,
        paddingX,
        nextWidth,
        scrollerClientWidth: scrollerEl.clientWidth,
        scrollerScrollWidth: scrollerEl.scrollWidth,
      });
    });
  }, [debugLabel]);

  useLayoutEffect(() => {
    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;
    let attachFrame: number | null = null;

    const attach = () => {
      if (cancelled) return;

      const hostEl = containerRef.current;
      const rootEl = rootRef.current;
      const asideEl = asideRef.current;
      const measureEl = measureRef.current;
      const viewportEl = viewportRef.current;

      if (!hostEl || !rootEl) {
        attachFrame = requestAnimationFrame(attach);
        return;
      }

      updateContainerWidth();
      resizeObserver = new ResizeObserver(() => updateContainerWidth());
      resizeObserver.observe(rootEl);
      if (asideEl) resizeObserver.observe(asideEl);
      if (measureEl) resizeObserver.observe(measureEl);
      if (viewportEl) resizeObserver.observe(viewportEl);
    };

    attach();
    window.addEventListener('resize', updateContainerWidth);
    window.addEventListener('orientationchange', updateContainerWidth);

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      if (attachFrame !== null) cancelAnimationFrame(attachFrame);
      if (widthRafRef.current !== null) cancelAnimationFrame(widthRafRef.current);
      window.removeEventListener('resize', updateContainerWidth);
      window.removeEventListener('orientationchange', updateContainerWidth);
    };
  }, [dialogOpen, isFullscreen, showThumbs, updateContainerWidth]);

  useEffect(() => {
    if (!dialogOpen) return;

    let followUpFrame: number | null = null;
    const initialFrame = requestAnimationFrame(() => {
      updateContainerWidth();
      followUpFrame = requestAnimationFrame(updateContainerWidth);
    });
    const settleTimer = window.setTimeout(updateContainerWidth, 220);

    return () => {
      cancelAnimationFrame(initialFrame);
      if (followUpFrame !== null) cancelAnimationFrame(followUpFrame);
      window.clearTimeout(settleTimer);
    };
  }, [dialogOpen, updateContainerWidth]);

  const pageWidth = fitWidth ? containerWidth : Math.max(280, Math.round(containerWidth * clampScale(scale)));

  // Auto-retry only when the page canvas never appears after load; avoid interrupting slow but valid document loads.
  useEffect(() => {
    if (!dialogOpen || !documentLoaded) return;
    if (renderedPage === pageNumber) return;
    const timer = window.setTimeout(() => {
      const shell = pageShellRef.current;
      const hasCanvas = Boolean(shell?.querySelector('.react-pdf__Page__canvas'));
      const retryKey = `${pageNumber}:${pageWidth}:${rotation}`;
      const attempts = pageRetryAttemptsRef.current[retryKey] ?? 0;

      if (!hasCanvas && renderedPage !== pageNumber && attempts < 2) {
        pageRetryAttemptsRef.current[retryKey] = attempts + 1;
        updateContainerWidth();
        setRenderAttempt((n) => n + 1);
      }
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [dialogOpen, documentLoaded, renderedPage, pageNumber, pageWidth, rotation, updateContainerWidth]);


  useEffect(() => {
    if (!documentLoaded) return;
    const raf = requestAnimationFrame(updateContainerWidth);
    return () => cancelAnimationFrame(raf);
  }, [documentLoaded, pageNumber, rotation, showThumbs, dialogOpen, updateContainerWidth]);

  useEffect(() => {
    if (!documentLoaded) return;
    setRenderedPage(null);
    setPageError(null);
  }, [documentLoaded, pageNumber, pageWidth, rotation]);

  const onDocumentLoadSuccess = (pdf: any) => {
    pdfDocRef.current = pdf;
    pageRetryAttemptsRef.current = {};
    setDocumentLoaded(true);
    setDocumentError(null);
    setNumPages(pdf.numPages);
    setPageNumber((currentPage) => Math.min(Math.max(1, currentPage), Math.max(1, pdf.numPages)));
    updateContainerWidth();
  };

  const handleDocumentError = (error: unknown) => {
    pdfDocRef.current = null;
    setDocumentLoaded(false);
    const message = formatError(error);
    if (/worker|font|translate|fetch/i.test(message) && advanceWorkerFallback()) {
      setDocAttempt((n) => n + 1);
      return;
    }
    setDocumentError(message);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  const toggleFullscreen = async () => {
    const el = rootRef.current;
    if (!el) return;

    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch {
      setIsFullscreen((value) => !value);
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const getDistance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    let isPinching = false;
    let pinchStartDistance = 0;
    let pinchStartScale = 1;

    const onStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) return;
      isPinching = true;
      pinchStartDistance = getDistance(event.touches);
      pinchStartScale = fitWidth ? 1 : scale;
    };

    const onMove = (event: TouchEvent) => {
      if (!isPinching || event.touches.length !== 2) return;
      event.preventDefault();
      const distance = getDistance(event.touches);
      const nextScale = clampScale(pinchStartScale * (distance / pinchStartDistance));
      if (fitWidth) setFitWidth(false);
      setScale(nextScale);
    };

    const onEnd = () => {
      isPinching = false;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [fitWidth, scale]);

  const runSearch = useCallback(async () => {
    const normalizedQuery = query.trim().toLowerCase();
    setHits([]);
    setHitIndex(0);

    if (!normalizedQuery || !pdfDocRef.current) return;

    setSearching(true);
    try {
      const found: SearchHit[] = [];
      const pdf = pdfDocRef.current;

      for (let page = 1; page <= pdf.numPages; page += 1) {
        const pdfPage = await pdf.getPage(page);
        const textContent = await pdfPage.getTextContent();
        const text = (textContent.items as Array<{ str: string }>).map((item) => item.str).join(' ').toLowerCase();

        let occurrence = 0;
        let from = 0;
        while ((from = text.indexOf(normalizedQuery, from)) !== -1) {
          found.push({ page, index: occurrence });
          occurrence += 1;
          from += normalizedQuery.length;
        }
      }

      setHits(found);
      if (found.length > 0) setPageNumber(found[0].page);
    } finally {
      setSearching(false);
    }
  }, [query]);

  const gotoHit = (delta: number) => {
    if (hits.length === 0) return;
    const nextIndex = (hitIndex + delta + hits.length) % hits.length;
    setHitIndex(nextIndex);
    setPageNumber(hits[nextIndex].page);
  };

  const dialogState = typeof dialogOpen === 'boolean' ? (dialogOpen ? 'open' : 'closed') : 'unknown';

  return (
    <div
      ref={rootRef}
      data-testid="pdf-viewer"
      className={
        isFullscreen
          ? 'fixed inset-0 z-[100] flex h-screen w-screen min-w-0 flex-col bg-background'
          : 'relative flex h-full min-h-0 min-w-0 w-full flex-col'
      }
    >
      <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b bg-background p-2 sm:p-3">
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant={showThumbs ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowThumbs((value) => !value)}
            title="Toggle thumbnails"
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
            disabled={pageNumber <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="whitespace-nowrap text-xs sm:text-sm">{pageNumber} / {numPages || '–'}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
            disabled={pageNumber >= numPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-1 sm:gap-2">
          <Button
            variant={showSearch ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowSearch((value) => !value)}
            title="Search"
          >
            <Search className="h-4 w-4" />
          </Button>
          <Button
            variant={showDebug ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowDebug((value) => !value)}
            title="Toggle PDF debug overlay"
          >
            <Bug className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRotation((value) => (value - 90 + 360) % 360)}
            title="Rotate left"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRotation((value) => (value + 90) % 360)}
            title="Rotate right"
          >
            <RotateCw className="h-4 w-4" />
          </Button>
          <Button
            variant={fitWidth ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFitWidth((value) => !value)}
            title="Fit to width"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFitWidth(false);
              setScale((value) => clampScale(value - 0.1));
            }}
            disabled={!fitWidth && scale <= 0.5}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="w-10 text-center text-xs sm:text-sm">{fitWidth ? 'Fit' : `${Math.round(scale * 100)}%`}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFitWidth(false);
              setScale((value) => clampScale(value + 0.1));
            }}
            disabled={!fitWidth && scale >= 3}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit full screen' : 'Full screen'}
          >
            {isFullscreen ? <Minimize className="h-4 w-4" /> : <Expand className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Download</span>
          </Button>
        </div>
      </div>

      {showSearch && (
        <div className="flex items-center gap-2 border-b bg-muted/30 px-2 py-2 sm:px-3">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                if (hits.length === 0) runSearch();
                else gotoHit(event.shiftKey ? -1 : 1);
              }
              if (event.key === 'Escape') setShowSearch(false);
            }}
            placeholder="Find in document…"
            className="h-8 max-w-xs"
          />
          <Button size="sm" variant="outline" onClick={runSearch} disabled={searching || !query.trim()}>
            {searching ? 'Searching…' : 'Find'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => gotoHit(-1)} disabled={hits.length === 0}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => gotoHit(1)} disabled={hits.length === 0}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">
            {hits.length === 0 ? (query && !searching ? 'No matches' : '') : `${hitIndex + 1} / ${hits.length} (page ${hits[hitIndex].page})`}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setShowSearch(false);
              setHits([]);
              setQuery('');
            }}
            className="ml-auto"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div className="relative flex min-h-0 min-w-0 w-full flex-1 overflow-hidden">
        <Document
          key={`doc-${docAttempt}`}
          file={url}
          options={docOptions}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={(error) => {
            advanceWorkerFallback();
            handleDocumentError(error);
          }}
          loading={null}
          error={null}
          className="flex min-h-0 min-w-0 w-full flex-1 overflow-hidden"
        >
          {showThumbs && numPages > 0 && (
            <aside ref={asideRef} className="w-32 shrink-0 space-y-2 overflow-y-auto border-r bg-muted/20 p-2 sm:w-40">
              {Array.from({ length: numPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setPageNumber(page)}
                  className={`block w-full overflow-hidden rounded border-2 bg-background transition-colors ${
                    page === pageNumber ? 'border-primary' : 'border-transparent hover:border-muted-foreground/30'
                  }`}
                  title={`Page ${page}`}
                >
                  <Page
                    pageNumber={page}
                    width={120}
                    rotate={rotation}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading={<div className="h-32" />}
                  />
                  <div className="py-1 text-center text-xs text-muted-foreground">{page}</div>
                </button>
              ))}
            </aside>
          )}

          <div ref={viewportRef} className="flex min-h-0 min-w-0 basis-0 flex-1 flex-col overflow-hidden">
            <div ref={measureRef} aria-hidden="true" className="h-0 w-full shrink-0 overflow-hidden" />
            <div
              ref={containerRef}
              data-testid="pdf-scroll-container"
              className="flex min-h-0 min-w-0 flex-1 items-start justify-center overflow-x-auto overflow-y-scroll overscroll-contain bg-muted/20 p-2 sm:p-4"
              style={{
                WebkitOverflowScrolling: 'touch',
                overflowY: 'scroll',
                scrollbarGutter: 'stable both-edges',
                touchAction: 'pan-x pan-y',
              }}
            >
                {documentError ? (
                  <div className="p-8 text-center">
                    <p className="text-destructive">Failed to load PDF</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={handleDownload}>
                      <Download className="mr-2 h-4 w-4" /> Download instead
                    </Button>
                  </div>
                ) : !documentLoaded ? (
                  <div className="flex items-center justify-center p-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
                  </div>
                ) : containerWidth > 0 ? (
                  <div ref={pageShellRef} className="max-w-full shrink-0" data-testid="pdf-page-shell">
                    <Page
                      key={`${pageNumber}-${rotation}-${renderAttempt}`}
                      pageNumber={pageNumber}
                      width={pageWidth}
                      rotate={rotation}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onRenderSuccess={() => {
                        setRenderedPage(pageNumber);
                        setPageError(null);
                        const shell = pageShellRef.current;
                        const canvas = shell?.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement | null;
                        console.info('[pdf-viewer] render-success', {
                          label: debugLabel,
                          pageNumber,
                          pageWidth,
                          shellWidth: shell?.getBoundingClientRect().width ?? null,
                          shellScrollWidth: shell?.scrollWidth ?? null,
                          canvasStyleWidth: canvas?.style.width ?? null,
                          canvasAttrWidth: canvas?.width ?? null,
                          canvasClientWidth: canvas?.clientWidth ?? null,
                        });
                      }}
                      onRenderError={(error) => {
                        const msg = formatError(error);
                        setPageError(msg);
                        if (/worker|font|translate|fetch/i.test(msg)) {
                          if (advanceWorkerFallback()) setDocAttempt((n) => n + 1);
                        }
                      }}
                      onLoadError={(error) => {
                        const msg = formatError(error);
                        setPageError(msg);
                        if (/worker|font|translate|fetch/i.test(msg)) {
                          if (advanceWorkerFallback()) setDocAttempt((n) => n + 1);
                        }
                      }}
                      loading={
                        <div className="flex items-center justify-center p-8">
                          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
                        </div>
                      }
                    />
                  </div>
                ) : null}
            </div>
          </div>
        </Document>

        {showDebug && (
          <div
            data-testid="pdf-debug-overlay"
            className="pointer-events-none absolute bottom-3 right-3 z-20 max-w-[min(22rem,calc(100%-1.5rem))] rounded-md border bg-background/95 p-3 text-xs shadow-lg backdrop-blur-sm"
          >
            <div className="mb-2 font-medium">{debugLabel} debug</div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-muted-foreground">
              <dt className="text-foreground">dialog</dt>
              <dd>{dialogState}</dd>
              <dt className="text-foreground">mounted</dt>
              <dd>{mounted ? 'yes' : 'no'}</dd>
              <dt className="text-foreground">root connected</dt>
              <dd>{rootRef.current?.isConnected ? 'yes' : 'no'}</dd>
              <dt className="text-foreground">measure</dt>
              <dd>{measurementStatus} via {measurementSource}</dd>
              <dt className="text-foreground">last width</dt>
              <dd>{lastMeasuredWidth}px</dd>
              <dt className="text-foreground">page width</dt>
              <dd>{pageWidth}px</dd>
              <dt className="text-foreground">doc loaded</dt>
              <dd>{documentLoaded ? 'yes' : 'no'}</dd>
              <dt className="text-foreground">page rendered</dt>
              <dd>{renderedPage ?? '—'}</dd>
              <dt className="text-foreground">page</dt>
              <dd>{pageNumber} / {numPages || '—'}</dd>
              <dt className="text-foreground">zoom</dt>
              <dd>{fitWidth ? 'fit-width' : `${Math.round(scale * 100)}%`}</dd>
              <dt className="text-foreground">rotation</dt>
              <dd>{rotation}°</dd>
              <dt className="text-foreground">doc error</dt>
              <dd className={documentError ? 'text-destructive' : ''}>{documentError ?? 'none'}</dd>
              <dt className="text-foreground">page error</dt>
              <dd className={pageError ? 'text-destructive' : ''}>{pageError ?? 'none'}</dd>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
};
